// Sprint 9.5 — RetentionHeatmap
// SVG inline heatmap retention coorti (D7/D30/D90).
//
// Layout: tabella verticale di coorti (asse Y = cohort_month) × 3 colonne (D7/D30/D90).
// Cella background = gradient verde scuro (alta retention) → rosso (bassa).
// Tooltip su hover con numeri assoluti.

import { useState } from 'react';
import { RetentionCohort } from '../lib/api';

interface RetentionHeatmapProps {
  cohorts: RetentionCohort[];
  /** Quante coorti recenti mostrare (default 8). */
  limit?: number;
}

function colorForPct(pct: number): string {
  // Gradient: 0% rosso scuro → 50% giallo → 100% verde
  if (pct >= 80) return '#0b8a3e';
  if (pct >= 60) return '#5fb37e';
  if (pct >= 40) return '#9bd2af';
  if (pct >= 20) return '#f5d680';
  return '#e8a06b';
}

function formatMonth(iso: string): string {
  // YYYY-MM → "Mar '26"
  const [y, m] = iso.split('-');
  const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
  const idx = parseInt(m, 10) - 1;
  if (idx < 0 || idx > 11) return iso;
  return `${months[idx]} '${y.slice(2)}`;
}

export function RetentionHeatmap({ cohorts, limit = 8 }: RetentionHeatmapProps) {
  const [hover, setHover] = useState<{ cohort: string; pct: number; raw: number; size: number; bucket: string; x: number; y: number } | null>(null);

  const recent = cohorts.slice(-limit);

  if (recent.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--mute, #888)', fontSize: 13 }}>
        Ancora nessuna coorte. I dati appariranno dopo i primi pazienti.
      </div>
    );
  }

  const ROW_H = 32;
  const HEADER_H = 30;
  const COL_LABEL_W = 110;
  const CELL_W = 80;
  const GAP = 4;
  const COLS = 3;
  const W = COL_LABEL_W + COLS * CELL_W + (COLS - 1) * GAP;
  const H = HEADER_H + recent.length * ROW_H + (recent.length - 1) * GAP;

  return (
    <div style={{ position: 'relative' }}>
      <svg
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        style={{ maxWidth: W, display: 'block' }}
        role="img"
        aria-label="Heatmap retention coorti"
      >
        {/* Header */}
        <g>
          <text x={0} y={20} fontSize={11} fontFamily="var(--mono, monospace)" fontWeight={600} fill="var(--mute, #888)">
            COORTE
          </text>
          {['D7', 'D30', 'D90'].map((label, i) => (
            <text
              key={label}
              x={COL_LABEL_W + i * (CELL_W + GAP) + CELL_W / 2}
              y={20}
              fontSize={11}
              fontFamily="var(--mono, monospace)"
              fontWeight={600}
              fill="var(--mute, #888)"
              textAnchor="middle"
            >
              {label}
            </text>
          ))}
        </g>
        {/* Rows */}
        {recent.map((c, rowIdx) => {
          const y = HEADER_H + rowIdx * (ROW_H + GAP);
          const cells = [
            { label: 'D7', pct: c.retention_d7_pct, raw: c.retained_d7 },
            { label: 'D30', pct: c.retention_d30_pct, raw: c.retained_d30 },
            { label: 'D90', pct: c.retention_d90_pct, raw: c.retained_d90 },
          ];
          return (
            <g key={c.cohort_month}>
              <text
                x={0}
                y={y + ROW_H / 2 + 4}
                fontSize={12}
                fontWeight={500}
                fill="var(--ink, #111)"
              >
                {formatMonth(c.cohort_month)}
              </text>
              <text
                x={0}
                y={y + ROW_H / 2 + 16}
                fontSize={9}
                fontFamily="var(--mono, monospace)"
                fill="var(--mute, #888)"
              >
                n={c.size}
              </text>
              {cells.map((cell, colIdx) => {
                const x = COL_LABEL_W + colIdx * (CELL_W + GAP);
                return (
                  <g
                    key={cell.label}
                    onMouseEnter={() =>
                      setHover({
                        cohort: c.cohort_month,
                        pct: cell.pct,
                        raw: cell.raw,
                        size: c.size,
                        bucket: cell.label,
                        x: x + CELL_W / 2,
                        y: y + ROW_H / 2,
                      })
                    }
                    onMouseLeave={() => setHover(null)}
                    style={{ cursor: 'pointer' }}
                  >
                    <rect
                      x={x}
                      y={y}
                      width={CELL_W}
                      height={ROW_H}
                      rx={4}
                      ry={4}
                      fill={colorForPct(cell.pct)}
                      stroke={hover && hover.cohort === c.cohort_month && hover.bucket === cell.label ? 'var(--ink, #111)' : 'none'}
                      strokeWidth={2}
                    />
                    <text
                      x={x + CELL_W / 2}
                      y={y + ROW_H / 2 + 4}
                      fontSize={12}
                      fontWeight={600}
                      fill={cell.pct >= 40 ? '#fff' : '#222'}
                      textAnchor="middle"
                    >
                      {cell.pct}%
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
      {hover && (
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            left: `${(hover.x / W) * 100}%`,
            top: `${(hover.y / H) * 100}%`,
            transform: 'translate(-50%, -100%) translateY(-8px)',
            background: 'var(--ink, #111)',
            color: '#fff',
            padding: '6px 10px',
            borderRadius: 6,
            fontSize: 11,
            fontFamily: 'var(--mono, monospace)',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            zIndex: 10,
            boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
          }}
        >
          {formatMonth(hover.cohort)} · {hover.bucket}: {hover.raw}/{hover.size} ({hover.pct}%)
        </div>
      )}
    </div>
  );
}
